/**
 * Centralized validation error messages
 * This file contains all validation error messages and format hints
 */

export interface ValidationMessageConfig {
  errorMessage: string;
  formatHint?: string;
}

/**
 * Default validation messages by validator key
 */
export const VALIDATION_MESSAGES: Record<string, ValidationMessageConfig> = {
  required: {
    errorMessage: '{label} is required'
  },
  email: {
    errorMessage: 'Please enter a valid email address',
    formatHint: 'Email should be in format: example@domain.com'
  },
  minLength: {
    errorMessage: 'Minimum length is {minLength} characters'
  },
  maxLength: {
    errorMessage: 'Maximum length is {maxLength} characters'
  },
  min: {
    errorMessage: 'Minimum value is {min}'
  },
  max: {
    errorMessage: 'Maximum value is {max}'
  },
  pattern: {
    errorMessage: '{label} format is invalid'
  },
  phone: {
    errorMessage: 'Please enter a valid phone number',
    formatHint: 'Phone should be in format: (123) 456-7890 or 123-456-7890'
  },
  url: {
    errorMessage: 'Please enter a valid URL',
    formatHint: 'URL should be in format: https://example.com'
  },
  alphanumeric: {
    errorMessage: '{label} must contain only letters and numbers'
  },
  numeric: {
    errorMessage: '{label} must contain only numbers'
  },
  matchField: {
    errorMessage: '{label} does not match'
  },
  fileType: {
    errorMessage: 'Invalid file type. Allowed types: {allowedTypes}'
  },
  fileSize: {
    errorMessage: 'File size exceeds the limit of {maxSize}MB'
  },
  requiredTrue: {
    errorMessage: '{label} must be accepted'
  }
};

/**
 * Get validation error message with parameter substitution
 */
export function getValidationMessage(
  validatorKey: string,
  params?: Record<string, any>,
  fieldLabel?: string
): string {
  const config = VALIDATION_MESSAGES[validatorKey];
  if (!config) {
    return `${fieldLabel || 'Field'} is invalid`;
  }

  let message = config.errorMessage;

  // Replace placeholders
  if (fieldLabel) {
    message = message.replace(/{label}/g, fieldLabel);
  }

  if (params) {
    Object.keys(params).forEach(key => {
      const placeholder = new RegExp(`{${key}}`, 'g');
      message = message.replace(placeholder, params[key]);
    });
  }

  return message;
}

/**
 * Get format hint for a validator
 */
export function getFormatHint(validatorKey: string): string | undefined {
  const config = VALIDATION_MESSAGES[validatorKey];
  return config?.formatHint;
}

/**
 * Get format hint for input type
 */
export function getInputTypeFormatHint(inputType: string): string | undefined {
  switch (inputType) {
    case 'email':
      return getFormatHint('email');
    case 'tel':
      return getFormatHint('phone');
    case 'url':
      return getFormatHint('url');
    default:
      return undefined;
  }
}
